export const careersWhyData = [
  {
    title: "Modern Technologies",
    desc: "Work with Laravel, PHP, Flutter, Vue, React, AWS and modern AI tools — always on the latest stack.",
    icon: "ri-stack-line",
  },
  {
    title: "Interesting Projects",
    desc: "Build real products across SaaS, healthcare, fintech, e-commerce, logistics and more.",
    icon: "ri-rocket-line",
  },
  {
    title: "Learning & Growth",
    desc: "Mentorship, training budgets and clear paths to grow your skills and career.",
    icon: "ri-graduation-cap-line",
  },
  {
    title: "Collaborative Culture",
    desc: "A supportive team that shares knowledge, reviews honestly and celebrates wins together.",
    icon: "ri-team-line",
  },
  {
    title: "Career Growth",
    desc: "Real ownership and progression — from engineer to senior, lead and beyond.",
    icon: "ri-line-chart-line",
  },
  {
    title: "Long-Term Opportunities",
    desc: "We invest in people who stay, grow and lead. Most of our team has grown within AstroJal.",
    icon: "ri-user-heart-line",
  },
];

export interface JobOpening {
  slug: string;
  title: string;
  experience: string;
  location: string;
  type: string;
  description: string;
  icon: string;
  color: string;
  responsibilities: string[];
  requirements: string[];
  niceToHave: string[];
  benefits: string[];
}

export const jobsData: JobOpening[] = [
  {
    slug: "laravel-developer",
    title: "Laravel Developer",
    experience: "2+ years",
    location: "Remote / San Francisco",
    type: "Full-time",
    description:
      "Build and maintain scalable Laravel applications, APIs and SaaS platforms with a focus on clean architecture and performance.",
    icon: "ri-code-s-slash-line",
    color: "#2563EB",
    responsibilities: [
      "Design and build scalable Laravel applications, REST APIs and SaaS platforms.",
      "Write clean, maintainable and well-tested code following best practices.",
      "Optimize database queries, caching and queue systems for performance.",
      "Integrate third-party APIs, payment gateways and AI services.",
      "Collaborate with frontend, mobile and DevOps teams on end-to-end features.",
      "Participate in code reviews and contribute to architecture decisions.",
    ],
    requirements: [
      "2+ years of professional Laravel and PHP development experience.",
      "Strong knowledge of MySQL, Eloquent, queues, caching and REST APIs.",
      "Experience with Git, testing and agile development workflows.",
      "Understanding of cloud deployment (AWS, Docker) is a plus.",
    ],
    niceToHave: [
      "Experience with Vue.js, React or Flutter.",
      "Familiarity with AI API integration (OpenAI, Claude).",
    ],
    benefits: [
      "Remote-friendly with flexible working hours.",
      "Learning budget and mentorship.",
      "Clear career growth path.",
    ],
  },
  {
    slug: "flutter-developer",
    title: "Flutter Developer",
    experience: "2+ years",
    location: "Remote",
    type: "Full-time",
    description:
      "Develop cross-platform mobile applications with Flutter, integrated with robust Laravel API backends.",
    icon: "ri-smartphone-line",
    color: "#7C3AED",
    responsibilities: [
      "Build cross-platform mobile apps for iOS and Android using Flutter.",
      "Integrate apps with Laravel REST APIs, push notifications and real-time features.",
      "Write clean, testable Dart code and maintain reusable widgets.",
      "Optimize app performance, animations and offline behavior.",
      "Collaborate with designers and backend engineers on product features.",
    ],
    requirements: [
      "2+ years of Flutter and Dart development experience.",
      "Strong understanding of state management (Provider, Riverpod or Bloc).",
      "Experience integrating REST APIs and handling auth flows.",
      "Familiarity with Git, CI/CD and app store releases.",
    ],
    niceToHave: [
      "Experience with Laravel or similar backend frameworks.",
      "Knowledge of native iOS/Android development.",
    ],
    benefits: [
      "Remote-friendly with flexible working hours.",
      "Learning budget and mentorship.",
      "Work on real consumer and enterprise products.",
    ],
  },
  {
    slug: "frontend-developer",
    title: "Frontend Developer",
    experience: "2+ years",
    location: "Remote / San Francisco",
    type: "Full-time",
    description:
      "Build modern, responsive interfaces with Vue.js or React, wired to high-performance backend APIs.",
    icon: "ri-window-line",
    color: "#F97316",
    responsibilities: [
      "Build responsive, accessible interfaces with Vue.js or React.",
      "Wire UI to REST APIs and manage application state.",
      "Translate Figma designs into pixel-perfect, performant components.",
      "Optimize frontend performance, bundling and load times.",
      "Work closely with backend engineers and designers.",
    ],
    requirements: [
      "2+ years of experience with Vue.js or React and modern JavaScript/TypeScript.",
      "Strong knowledge of Tailwind CSS, CSS and responsive design.",
      "Experience with state management and API integration.",
      "Understanding of web performance and accessibility.",
    ],
    niceToHave: [
      "Experience with Laravel Blade or Inertia.js.",
      "Familiarity with Flutter or mobile development.",
    ],
    benefits: [
      "Remote-friendly with flexible working hours.",
      "Learning budget and mentorship.",
      "Modern, maintainable codebase and design system.",
    ],
  },
  {
    slug: "backend-developer",
    title: "Backend Developer",
    experience: "2+ years",
    location: "Remote",
    type: "Full-time",
    description:
      "Design and build secure, scalable backend systems, APIs and data architectures for web and mobile products.",
    icon: "ri-server-line",
    color: "#0EA5E9",
    responsibilities: [
      "Design and build scalable backend services, REST APIs and integrations.",
      "Model databases and optimize queries for performance and scale.",
      "Implement authentication, authorization and security best practices.",
      "Integrate third-party APIs, payment gateways and AI services.",
      "Write clean, well-tested code and maintain API documentation.",
    ],
    requirements: [
      "2+ years of backend development experience (PHP, Node.js or similar).",
      "Strong knowledge of SQL databases and REST API design.",
      "Understanding of caching, queues, queues and background jobs.",
      "Experience with Git, testing and agile workflows.",
    ],
    niceToHave: [
      "Experience with Laravel, Django or NestJS.",
      "Familiarity with AWS, Docker and CI/CD.",
    ],
    benefits: [
      "Remote-friendly with flexible working hours.",
      "Learning budget and mentorship.",
      "Ownership over backend architecture.",
    ],
  },
  {
    slug: "qa-engineer",
    title: "QA Engineer",
    experience: "2+ years",
    location: "Remote",
    type: "Full-time",
    description:
      "Own quality across our products — automated and manual testing, test planning and release readiness.",
    icon: "ri-bug-line",
    color: "#F59E0B",
    responsibilities: [
      "Design and execute test plans across web, mobile and API products.",
      "Build and maintain automated test suites and regression testing.",
      "Identify, document and track bugs through to resolution.",
      "Collaborate with engineers to improve testability and code quality.",
      "Verify releases and support production incident investigation.",
    ],
    requirements: [
      "2+ years of QA engineering experience.",
      "Experience with automated testing tools and frameworks.",
      "Strong understanding of test planning, regression and API testing.",
      "Familiarity with CI/CD and bug-tracking tools (Jira).",
    ],
    niceToHave: [
      "Experience with Laravel or PHP testing (PHPUnit, Pest).",
      "Knowledge of Flutter or mobile testing.",
    ],
    benefits: [
      "Remote-friendly with flexible working hours.",
      "Learning budget and mentorship.",
      "Direct impact on product quality.",
    ],
  },
  {
    slug: "project-manager",
    title: "Project Manager",
    experience: "3+ years",
    location: "Remote / San Francisco",
    type: "Full-time",
    description:
      "Coordinate cross-functional teams, timelines and client communication to deliver successful product outcomes.",
    icon: "ri-briefcase-line",
    color: "#10B981",
    responsibilities: [
      "Lead project planning, scoping and delivery across engineering teams.",
      "Manage timelines, budgets, risks and stakeholder expectations.",
      "Facilitate agile ceremonies and maintain clear documentation.",
      "Act as the bridge between clients and the development team.",
      "Drive continuous improvement in process and delivery quality.",
    ],
    requirements: [
      "3+ years of project or product management experience in software.",
      "Strong communication, organization and problem-solving skills.",
      "Experience with agile methodologies and project tools (Jira, Trello).",
      "Ability to manage multiple priorities and remote teams.",
    ],
    niceToHave: [
      "Technical background in web or mobile development.",
      "Experience managing client-facing software delivery.",
    ],
    benefits: [
      "Remote-friendly with flexible working hours.",
      "Learning budget and mentorship.",
      "Opportunity to shape delivery processes.",
    ],
  },
];

export const hiringProcessData = [
  { step: "01", title: "Application", desc: "Apply with your resume and a short note about your experience." },
  { step: "02", title: "Review", desc: "Our team reviews your application and shortlists strong matches." },
  { step: "03", title: "Discussion", desc: "A friendly conversation about your skills, goals and our team." },
  { step: "04", title: "Technical Evaluation", desc: "A practical session to understand how you approach problems." },
  { step: "05", title: "Final Discussion", desc: "Meet the team, align on role, expectations and next steps." },
  { step: "06", title: "Join AstroJal", desc: "If it's a great fit, we'll make you an offer to join us." },
];

export const careersStatsData = [
  { value: "20+", label: "Team Members" },
  { value: "50+", label: "Projects Delivered" },
  { value: "10+", label: "Technologies Mastered" },
  { value: "100%", label: "Remote-Friendly" },
];